import _ from '@luna/lodash';
import { getEnv } from '@luna/env';
import type { DeepPartial } from '@luna/utils-ts';

import {
    getFeatureConfigWithoutOverrides,
    setFeatureConfigSingleton,
} from './featureConfigSingleton';
import { parseFeatureConfigFromStorage, parseFeatureConfigFromUrl } from './featureConfigService';

// @exclude-jsdoc-to-md

// @ts-expect-error no-implicit-any
function arrayOverwrite(a, b) {
    return Array.isArray(a) ? b : undefined;
}

// @ts-expect-error no-implicit-any
export const getCountryFeatureConfig = (countryFeatureConfigMap): any => {
    return countryFeatureConfigMap[getEnv().country] ?? {};
};

const getConfigWithoutOverrides = (featureConfig: unknown, key?: string): unknown => {
    const existingFeatureConfig = _.cloneComplexStructureDeep(
        getFeatureConfigWithoutOverrides() ?? {},
    );

    if (!key) {
        _.merge(existingFeatureConfig, featureConfig, arrayOverwrite);
        return existingFeatureConfig;
    }

    _.merge(existingFeatureConfig, { [key]: featureConfig }, arrayOverwrite);
    return existingFeatureConfig;
};

const getOverridesSafely = (): unknown => {
    if (getEnv()?.loadOverridesFromUrlAndStorage) {
        return _.merge(parseFeatureConfigFromStorage(), parseFeatureConfigFromUrl());
    }

    if (getEnv()?.environment === 'uat' && getEnv()?.allowUatOverrides) {
        return _.merge(parseFeatureConfigFromStorage());
    }

    return {};
};

export const registerFeatureConfig = (featureConfig: unknown, key?: string): void => {
    const overrides = getOverridesSafely();

    const configWithoutOverrides = getConfigWithoutOverrides(featureConfig, key);

    const finalFeatureConfig = _.cloneComplexStructureDeep(configWithoutOverrides);

    _.merge(finalFeatureConfig, overrides, arrayOverwrite);

    setFeatureConfigSingleton({
        config: finalFeatureConfig,
        configWithoutOverrides,
    });
};

export type FeatureConfigMap<T = unknown> = {
    base: T extends unknown ? Record<string, any> : T;
    [key: string]: T extends unknown ? Record<string, any> : DeepPartial<T>;
};

/**
 * Registers a feature configuration map for a specific feature or group of features.
 *
 * This function takes a country-specific feature configuration map and registers it
 * with the global feature configuration. It first registers the base configuration,
 * and then overlays the country-specific configuration on top.
 *
 * @param {FeatureConfigMap<T>} countryFeatureConfigMap - An object containing feature configurations.
 *   It should have a 'base' property with the default configuration, and additional
 *   country-specific configurations (e.g., AT, CZ, SK, etc.).
 * @param {string} [key] - A key to namespace the configuration. The configuration
 *   will be registered under this key in the global configuration.
 *
 * @remarks
 * - It will merge the base configuration with the country-specific configuration.
 * - The configuration is namespaced under the provided key.
 * - Country-specific configurations can be partial, overriding only specific properties.
 * - This function also handles overrides from URL and local storage if enabled in the environment.
 * - Use DeepPartial<T> for country-specific configurations to allow partial overrides.
 */
export const registerFeatureConfigMap = (
    countryFeatureConfigMap: FeatureConfigMap,
    key?: string,
): void => {
    registerFeatureConfig(countryFeatureConfigMap.base, key);
    registerFeatureConfig(getCountryFeatureConfig(countryFeatureConfigMap), key);
};
